/**
 * Particles.
 *
 * A fixed-size CPU pool of point sprites — vanilla draws every particle as a
 * camera-facing quad too, so a point sprite is the right primitive. Two kinds
 * live in the pool:
 *
 *  - block chips: a broken block spews bits whose colour is sampled from that
 *    block's atlas tile (vanilla's "block" particle does exactly this), with
 *    gravity and a cheap voxel bounce,
 *  - effect sprites: one frame of an official Minecraft particle sequence —
 *    `generic_0..7` for the smoke a primed charge fizzes, `explosion_0..15` for
 *    the blast cloud. These walk their own frame sequence as they age, the way
 *    vanilla's setSpriteFromAge does, and they carry their own gravity, so
 *    chips arc, smoke drifts up and poofs hang where they were born.
 *
 * The sprites come from particleTextures.ts, which falls through to a
 * procedural painter when the extracted game assets are missing.
 */

import * as THREE from 'three';
import { BLOCKS, TINTABLE_TILES, tintOf } from '../blocks';
import { ATLAS_COLS, ATLAS_H, ATLAS_W, CELL, GUTTER, TILE } from '../atlasLayout';
import { MASKED_TINT_TILES } from '../biomeTint';
import type { EnvUniforms } from './env';
import { blockLightTint, lightmap } from './lightmap';
import type { World } from '../world/chunk';
import type { ParticleTextures } from './particleTextures';

const POOL = 1024;
/** Terrain chips fall at world gravity; other particles carry their own. */
const CHIP_GRAVITY = 22;

const VERT = /* glsl */ `
uniform float uViewH;
in float aSize;
in vec4 aColor;
in float aTile;
in vec2 aUv;
out vec4 vColor;
out float vTile;
out vec2 vUv;
void main() {
  vColor = aColor;
  vTile = aTile;
  vUv = aUv;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  // terrain chips carry a true world size (blocks); effect sprites keep their tuned scale
  gl_PointSize = aTile < -1.5
    ? aSize * projectionMatrix[1][1] * uViewH * 0.5 / max(-mv.z, 0.05)
    : aSize * (260.0 / -mv.z);
  gl_Position = projectionMatrix * mv;
}`;

const FRAG = /* glsl */ `
precision highp float;
precision highp sampler2DArray;
uniform sampler2DArray uSmoke;
uniform sampler2DArray uPoof;
// layer index the poof frames start at (the smoke frames sit below it)
uniform float uPoofBase;
uniform sampler2D uAtlas;
uniform vec2 uSub; // a quarter tile, in atlas uv
in vec4 vColor;
in float vTile;
in vec2 vUv;
out vec4 fragColor;
void main() {
  if (vTile < -1.5) {
    // vanilla TerrainParticle: a random 4x4-texel corner of the block's sprite
    vec4 t = texture(uAtlas, vUv + gl_PointCoord * uSub);
    if (t.a < 0.5) discard;
    fragColor = vec4(t.rgb * vColor.rgb, 1.0);
    return;
  }
  if (vTile < 0.0) {
    // block chip: the sampled texel colour, no sprite
    fragColor = vColor;
    return;
  }
  // gl_PointCoord runs top-down, texture space bottom-up
  vec2 uv = vec2(gl_PointCoord.x, 1.0 - gl_PointCoord.y);
  vec4 tex = vTile < uPoofBase
    ? texture(uSmoke, vec3(uv, vTile))
    : texture(uPoof, vec3(uv, vTile - uPoofBase));
  fragColor = tex * vColor;
  if (fragColor.a < 0.02) discard;
}`;

interface Particle {
  vx: number;
  vy: number;
  vz: number;
  life: number;
  maxLife: number;
  size: number;
  /** Downward acceleration, blocks/s^2 (negative = drifts up). */
  gravity: number;
  /** World collision + bounce (block chips only). */
  collides: boolean;
  /** First layer of the particle's sprite sequence, or -1 for a block chip. */
  first: number;
  frames: number;
  /** Layer currently written into `aTile`. */
  layer: number;
  /** Vanilla terrain physics (per-tick drag, ground friction, no bounce). */
  terrain: boolean;
  onGround: boolean;
}

/** A blast's poof cloud is seeded over several ticks, like vanilla's emitter. */
interface Emitter {
  x: number;
  y: number;
  z: number;
  ticks: number;
}

export class BlockParticles {
  readonly points: THREE.Points;
  private geom: THREE.BufferGeometry;
  private pos: Float32Array;
  private col: Float32Array;
  private sizeAttr: Float32Array;
  private tileAttr: Float32Array;
  private uvAttr: Float32Array;
  /** Lighting source for terrain chips (time of day + Brightness); set by the game. */
  env: EnvUniforms | null = null;
  private readonly atlasTex: THREE.CanvasTexture;
  private parts: Particle[] = [];
  private cursor = 0;
  private readonly emitters: Emitter[] = [];
  /** Frames of emitter age not yet turned into poofs (vanilla emits per tick). */
  private emitAcc = 0;

  constructor(
    private readonly world: World,
    atlas: HTMLCanvasElement,
    private readonly sprites: ParticleTextures,
  ) {
    this.pos = new Float32Array(POOL * 3);
    this.col = new Float32Array(POOL * 4);
    this.sizeAttr = new Float32Array(POOL);
    this.tileAttr = new Float32Array(POOL).fill(-1);
    this.uvAttr = new Float32Array(POOL * 2);
    for (let i = 0; i < POOL; i++) {
      this.parts.push({
        vx: 0, vy: 0, vz: 0, life: 0, maxLife: 1, size: 0,
        gravity: CHIP_GRAVITY, collides: true, first: -1, frames: 0, layer: -1, terrain: false, onGround: false,
      });
    }


    this.geom = new THREE.BufferGeometry();
    this.geom.setAttribute('position', new THREE.BufferAttribute(this.pos, 3));
    this.geom.setAttribute('aColor', new THREE.BufferAttribute(this.col, 4));
    this.geom.setAttribute('aSize', new THREE.BufferAttribute(this.sizeAttr, 1));
    this.geom.setAttribute('aTile', new THREE.BufferAttribute(this.tileAttr, 1));
    this.geom.setAttribute('aUv', new THREE.BufferAttribute(this.uvAttr, 2));
    this.geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    const mat = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uSmoke: { value: sprites.smoke },
        uPoof: { value: sprites.poof },
        uPoofBase: { value: sprites.smokeFrames },
        uAtlas: { value: null as THREE.Texture | null },
        uSub: { value: new THREE.Vector2(TILE / 4 / ATLAS_W, TILE / 4 / ATLAS_H) },
        uViewH: { value: 540 },
      },
      transparent: true,
      depthWrite: false,
    });
    this.points = new THREE.Points(this.geom, mat);
    this.points.frustumCulled = false;
    this.atlasTex = new THREE.CanvasTexture(atlas);
    this.atlasTex.colorSpace = THREE.SRGBColorSpace;
    this.atlasTex.flipY = false;
    this.atlasTex.magFilter = this.atlasTex.minFilter = THREE.NearestFilter;
    this.atlasTex.generateMipmaps = false;
    mat.uniforms.uAtlas.value = this.atlasTex;
    const vs = new THREE.Vector2();
    this.points.onBeforeRender = (r) => { mat.uniforms.uViewH.value = r.getDrawingBufferSize(vs).y; };
  }

  /**
   * Break burst, vanilla ParticleEngine.destroy: a 4x4x4 grid of terrain
   * particles, each pushed away from the block centre.
   */
  burst(x: number, y: number, z: number, blockId: number): void {
    const src = this.terrainSource(blockId);
    if (!src) return;
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        for (let k = 0; k < 4; k++) {
          const fx = (i + 0.5) / 4, fy = (j + 0.5) / 4, fz = (k + 0.5) / 4;
          this.terrain(src, x + fx, y + fy, z + fz, fx - 0.5, fy - 0.5, fz - 0.5, 1, 1);
        }
      }
    }
  }

  private crackClock = 0;
  /**
   * Mining hit particles (vanilla ParticleEngine.crack): one per game tick on
   * the struck face, inset 0.1 from its edges and 0.1 out along the normal,
   * with the motion scaled by 0.2 and the size by 0.6.
   */
  crack(x: number, y: number, z: number, blockId: number, nx: number, ny: number, nz: number, dt: number): void {
    this.crackClock += dt;
    if (this.crackClock < 0.05) return;
    this.crackClock = Math.min(this.crackClock - 0.05, 0.05);
    const src = this.terrainSource(blockId);
    if (!src) return;
    const r = (): number => 0.1 + Math.random() * 0.8;
    const px = x + (nx > 0 ? 1.1 : nx < 0 ? -0.1 : r());
    const py = y + (ny > 0 ? 1.1 : ny < 0 ? -0.1 : r());
    const pz = z + (nz > 0 ? 1.1 : nz < 0 ? -0.1 : r());
    this.terrain(src, px, py, pz, 0, 0, 0, 0.2, 0.6);
  }

  /**
   * Vanilla "portal" particle stand-in: a small chip of the block's texture
   * that drifts toward (tx, ty, tz) without gravity (nether portals, enderman
   * teleports).
   */
  portal(blockId: number, x: number, y: number, z: number, tx: number, ty: number, tz: number): void {
    const src = this.terrainSource(blockId);
    if (!src) return;
    this.terrain(src, x, y, z, 0, 0, 0, 0.2, 0.5);
    const p = this.parts[(this.cursor + POOL - 1) % POOL];
    p.gravity = 0;
    p.collides = false;
    p.maxLife = p.life = 0.8 + Math.random() * 0.8;
    p.vx = (tx - x) / p.life; p.vy = (ty - y) / p.life; p.vz = (tz - z) / p.life;
  }

  /** One flat-coloured, unlit speck (hit stars, crumbs): velocity in blocks/s, no collision. */
  chip(x: number, y: number, z: number, vx: number, vy: number, vz: number,
    r: number, g: number, b: number, size: number, life: number, gravity = 0): void {
    const i = this.spawn();
    const p = this.parts[i];
    p.vx = vx; p.vy = vy; p.vz = vz;
    p.maxLife = p.life = life;
    p.size = size; p.gravity = gravity;
    p.collides = false; p.first = -1; p.frames = 0; p.layer = -1;
    this.put(i, x, y, z, r, g, b);
  }

  /** A few terrain specks kicked up by a sprinting or landing foot (vanilla sprint/land particles). */
  dust(x: number, y: number, z: number, blockId: number, count: number, power = 1): void {
    const src = this.terrainSource(blockId);
    if (!src) return;
    for (let n = 0; n < count; n++) this.terrain(src, x + (Math.random() - 0.5) * 0.6, y + 0.05, z + (Math.random() - 0.5) * 0.6,
      (Math.random() - 0.5) * 0.4, 0.2, (Math.random() - 0.5) * 0.4, power, 0.8);
  }

  /** Atlas tile + tint for a block's particles, or null if it has none. */
  private terrainSource(blockId: number): { tile: number; tint: [number, number, number] } | null {
    const def = BLOCKS[blockId];
    if (!def) return null;
    // a masked side (grass) is mostly the dirt underneath: use the bottom tile
    let tile = def.tiles[0];
    if (MASKED_TINT_TILES[tile] === 1) tile = def.tiles[3];
    const t = TINTABLE_TILES[tile] === 1 ? tintOf(blockId) : null;
    // tints are authored in sRGB; the atlas is decoded to linear in the shader
    const lin = (v: number): number => Math.pow(v, 2.2);
    return { tile, tint: t ? [lin(t[0]), lin(t[1]), lin(t[2])] : [1, 1, 1] };
  }

  /** Vanilla Particle(x,y,z,xa,ya,za) + TerrainParticle: velocities in blocks/tick. */
  private terrain(
    src: { tile: number; tint: [number, number, number] },
    px: number, py: number, pz: number, xa: number, ya: number, za: number, power: number, scale: number,
  ): void {
    let vx = xa + (Math.random() * 2 - 1) * 0.4;
    let vy = ya + (Math.random() * 2 - 1) * 0.4;
    let vz = za + (Math.random() * 2 - 1) * 0.4;
    const f = (Math.random() + Math.random() + 1) * 0.15;
    const d = Math.hypot(vx, vy, vz) || 1;
    vx = (vx / d) * f * 0.4;
    vy = (vy / d) * f * 0.4 + 0.1;
    vz = (vz / d) * f * 0.4;
    // setPower
    vx *= power; vy = (vy - 0.1) * power + 0.1; vz *= power;
    const i = this.spawn();
    const p = this.parts[i];
    p.vx = vx * 20; p.vy = vy * 20; p.vz = vz * 20; // per second
    p.maxLife = Math.floor(4 / (Math.random() * 0.9 + 0.1)) / 20;
    p.life = p.maxLife;
    // quadSize 0.1*(r*0.5+0.5)*2 halved by TerrainParticle; the full quad is twice that
    p.size = 0.1 * (Math.random() * 0.5 + 0.5) * 2 * scale;
    p.gravity = 16; // 0.04 blocks/tick^2
    p.collides = true;
    p.terrain = true;
    p.onGround = false;
    p.first = -2;
    p.frames = 0;
    p.layer = -2;
    // light: one lightmap sample at the spawn point (vanilla re-samples per frame; chips live < 2 s)
    const bx = Math.floor(px), by = Math.floor(py), bz = Math.floor(pz);
    const day = this.env?.uDayLight.value ?? 1;
    const gamma = this.env?.uGamma.value ?? 0.5;
    const sky = lightmap((this.world.getSky(bx, by, bz) * (0.22 + 0.78 * day)) / 15, gamma);
    const blk = blockLightTint(lightmap(this.world.getBlockLight(bx, by, bz) / 15, gamma));
    // TerrainParticle colour 0.6, scaled to the voxel shader's sunlit-top level
    const k = 0.6 * 1.25;
    const lr = Math.max(sky, blk[0]) * k + 0.01, lg = Math.max(sky, blk[1]) * k + 0.01, lb = Math.max(sky, blk[2]) * k + 0.01;
    const col = src.tile % ATLAS_COLS, row = (src.tile / ATLAS_COLS) | 0;
    this.uvAttr[i * 2] = (col * CELL + GUTTER + Math.floor(Math.random() * (TILE * 0.75 + 1))) / ATLAS_W;
    this.uvAttr[i * 2 + 1] = (row * CELL + GUTTER + Math.floor(Math.random() * (TILE * 0.75 + 1))) / ATLAS_H;
    this.uvDirty = true;
    this.put(i, px, py, pz, src.tint[0] * lr, src.tint[1] * lg, src.tint[2] * lb);
  }
  private uvDirty = false;

  /**
   * One SMOKE puff off the top of a primed charge. Vanilla's PrimedTnt.tick
   * adds exactly this every game tick: a `generic_*` puff somewhere in the
   * 0.5 block square above the entity, with no initial velocity.
   */
  fuse(x: number, y: number, z: number): void {
    const i = this.spawn();
    const p = this.parts[i];
    p.vx = 0;
    p.vy = 0;
    p.vz = 0;
    // vanilla smoke lives 10..40 ticks and drifts up as it thins out
    p.maxLife = 0.5 + Math.random() * 1.5;
    p.life = p.maxLife;
    p.size = 0.45; // an 8x8 sprite drawn about 3x, which is how vanilla scales it
    p.gravity = -1.2;
    p.collides = false;
    p.first = 0;
    p.frames = this.sprites.smokeFrames;
    p.layer = -1;
    this.put(i, x + (Math.random() - 0.5) * 0.5, y, z + (Math.random() - 0.5) * 0.5, 1, 1, 1);
  }

  /** One firework spark: a tinted puff flying out from a burst (rgb 0..1 each). */
  spark(x: number, y: number, z: number, vx: number, vy: number, vz: number, r: number, g: number, b: number): void {
    const i = this.spawn();
    const p = this.parts[i];
    p.vx = vx; p.vy = vy; p.vz = vz;
    p.maxLife = 0.6 + Math.random() * 0.5;
    p.life = p.maxLife;
    p.size = 0.2;
    p.gravity = 1.5;
    p.collides = false;
    p.first = 0;
    p.frames = this.sprites.smokeFrames;
    p.layer = -1;
    this.put(i, x, y, z, r * 1.6, g * 1.6, b * 1.6);
  }

  /** One furnace flame: a small, short-lived orange flicker (vanilla's FLAME particle), drifting up. */
  flame(x: number, y: number, z: number): void {
    const i = this.spawn();
    const p = this.parts[i];
    p.vx = 0;
    p.vy = 0.15;
    p.vz = 0;
    p.maxLife = 0.2 + Math.random() * 0.25;
    p.life = p.maxLife;
    p.size = 0.14;
    p.gravity = 0;
    p.collides = false;
    p.first = 0;
    p.frames = this.sprites.smokeFrames;
    p.layer = -1;
    this.put(i, x, y, z, 2.2, 1.1, 0.25);
  }

  /** One purple dragon's-breath wisp (vanilla's dragon_breath particle: a tinted puff drifting up). */
  breath(x: number, y: number, z: number): void {
    const i = this.spawn();
    const p = this.parts[i];
    p.vx = (Math.random() - 0.5) * 0.15;
    p.vy = 0.25 + Math.random() * 0.2;
    p.vz = (Math.random() - 0.5) * 0.15;
    p.maxLife = 1 + Math.random() * 1.2;
    p.life = p.maxLife;
    p.size = 0.3 + Math.random() * 0.2;
    p.gravity = 0;
    p.collides = false;
    p.first = 0;
    p.frames = this.sprites.smokeFrames;
    p.layer = -1;
    const t = Math.random();
    this.put(i, x, y, z, 0.55 + 0.25 * t, 0.12 + 0.1 * t, 0.85 + 0.15 * t);
  }

  /**
   * A blast cloud, vanilla's way: an explosion emitter at the centre which
   * seeds six EXPLOSION poofs a tick for eight ticks, scattered through a +/-4
   * block box around it — each poof swelling through its sprite sequence as it
   * ages, which is what the classic TNT cloud is made of.
   */
  explosion(cx: number, cy: number, cz: number, power: number): void {
    if (power <= 0) return;
    this.emitters.push({ x: cx, y: cy, z: cz, ticks: 8 });
  }

  update(dt: number): void {
    this.seedBlast(dt);
    let dirty = false;
    let tilesDirty = false;
    for (let i = 0; i < POOL; i++) {
      const p = this.parts[i];
      if (p.life <= 0) continue;
      dirty = true;
      p.life -= dt;
      if (p.life <= 0) {
        this.sizeAttr[i] = 0;
        this.col[i * 4 + 3] = 0;
        continue;
      }
      // sprite sequences walk their frames with age, like vanilla's
      // setSpriteFromAge: the smoke puff grows, the poof swells
      if (p.frames > 0) {
        const frame = Math.min(p.frames - 1, Math.floor((1 - p.life / p.maxLife) * p.frames));
        const layer = p.first + frame;
        if (layer !== p.layer) {
          p.layer = layer;
          this.tileAttr[i] = layer;
          tilesDirty = true;
        }
      }
      if (p.terrain) {
        // vanilla Particle.tick: gravity, move with collision, drag 0.98, ground friction 0.7
        const t = dt * 20;
        p.vy -= p.gravity * dt;
        const nx = this.pos[i * 3] + p.vx * dt, ny = this.pos[i * 3 + 1] + p.vy * dt, nz = this.pos[i * 3 + 2] + p.vz * dt;
        const solidAt = (x: number, y: number, z: number): boolean =>
          y >= 0 && BLOCKS[this.world.getBlock(Math.floor(x), Math.floor(y), Math.floor(z))]?.solid === true;
        p.onGround = false;
        if (solidAt(this.pos[i * 3], ny, this.pos[i * 3 + 2])) {
          if (p.vy < 0) p.onGround = true;
          p.vy = 0;
        } else this.pos[i * 3 + 1] = ny;
        if (solidAt(nx, this.pos[i * 3 + 1], this.pos[i * 3 + 2])) p.vx = 0; else this.pos[i * 3] = nx;
        if (solidAt(this.pos[i * 3], this.pos[i * 3 + 1], nz)) p.vz = 0; else this.pos[i * 3 + 2] = nz;
        const drag = Math.pow(0.98, t);
        p.vx *= drag; p.vy *= drag; p.vz *= drag;
        if (p.onGround) { const fr = Math.pow(0.7, t); p.vx *= fr; p.vz *= fr; }
        continue;
      }
      p.vy -= p.gravity * dt;
      if (p.collides) {
        const ix = Math.floor(this.pos[i * 3]);
        const iy = Math.floor(this.pos[i * 3 + 1] + (p.vy * dt));
        const iz = Math.floor(this.pos[i * 3 + 2]);
        const ground = iy >= 0 ? BLOCKS[this.world.getBlock(ix, iy, iz)] : undefined;
        if (p.vy < 0 && ground?.solid) {
          p.vy *= -0.3;
          p.vx *= 0.6;
          p.vz *= 0.6;
        }
      }
      this.pos[i * 3] += p.vx * dt;
      this.pos[i * 3 + 1] += p.vy * dt;
      this.pos[i * 3 + 2] += p.vz * dt;
      // no fade — vanilla particles just pop out of existence
    }
    if (dirty) {
      (this.geom.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
      (this.geom.getAttribute('aColor') as THREE.BufferAttribute).needsUpdate = true;
      (this.geom.getAttribute('aSize') as THREE.BufferAttribute).needsUpdate = true;
    }
    if (tilesDirty || this.uvDirty) (this.geom.getAttribute('aTile') as THREE.BufferAttribute).needsUpdate = true;
    if (this.uvDirty) { (this.geom.getAttribute('aUv') as THREE.BufferAttribute).needsUpdate = true; this.uvDirty = false; }
  }

  /** Turn each emitter's age into poofs: vanilla seeds per game tick, not per frame. */
  private seedBlast(dt: number): void {
    if (this.emitters.length === 0) return;
    this.emitAcc += dt * 20;
    let ticks = Math.floor(this.emitAcc);
    if (ticks <= 0) return;
    this.emitAcc -= ticks;
    for (let e = this.emitters.length - 1; e >= 0 && ticks > 0; e--) {
      const em = this.emitters[e];
      for (; ticks > 0 && em.ticks > 0; ticks--, em.ticks--) {
        for (let n = 0; n < 6; n++) this.poof(em);
      }
      if (em.ticks <= 0) this.emitters.splice(e, 1);
    }
  }

  private poof(em: Emitter): void {
    const i = this.spawn();
    const p = this.parts[i];
    p.vx = 0;
    p.vy = 0;
    p.vz = 0;
    p.maxLife = 0.3 + Math.random() * 0.15; // vanilla: 6..9 ticks
    p.life = p.maxLife;
    // vanilla scales a poof up to about two blocks across
    p.size = 1.2 + Math.random() * 1.8;
    p.gravity = 0;
    p.collides = false;
    p.first = this.sprites.smokeFrames;
    p.frames = this.sprites.poofFrames;
    p.layer = -1;
    // vanilla scatters the cloud over a +/-4 block box
    const spread = (): number => (Math.random() - Math.random()) * 4;
    this.put(i, em.x + spread(), em.y + spread(), em.z + spread(), 1, 1, 1);
  }

  /** Take the next pool slot, clearing it so a stale frame cannot leak. */
  private spawn(): number {
    const i = this.cursor;
    this.cursor = (this.cursor + 1) % POOL;
    this.sizeAttr[i] = 0;
    this.col[i * 4 + 3] = 0;
    this.tileAttr[i] = -1;
    this.parts[i].terrain = false;
    return i;
  }

  /** Write one particle's slot. */
  private put(
    i: number, px: number, py: number, pz: number,
    r: number, g: number, b: number,
  ): void {
    const p = this.parts[i];
    this.pos[i * 3] = px;
    this.pos[i * 3 + 1] = py;
    this.pos[i * 3 + 2] = pz;
    this.col[i * 4] = r;
    this.col[i * 4 + 1] = g;
    this.col[i * 4 + 2] = b;
    this.col[i * 4 + 3] = 1;
    this.sizeAttr[i] = p.size;
    this.tileAttr[i] = p.first;
    p.layer = p.first;
  }
}
